/**
 * AP-32 13.3 — **Das Positionsraster.**
 *
 * Tastatur: Tab durch die Felder · Enter springt in dieselbe Spalte der nächsten
 * Zeile (Nachtrag 11 — es legt **keine** Zeile an) · Alt+→ macht
 * die Zeile zur Unterposition der darüberliegenden, Alt+← hebt sie heraus ·
 * Alt+↑/↓ verschiebt sie mitsamt ihren Kindern · Esc verlässt das Feld und
 * markiert die Zeile · Strg+C/Strg+V wirken auf Zeilen, **sobald kein Feld in
 * Bearbeitung ist** (Fokusregel 5.8) — im Feld bleibt es gewöhnliches
 * Textkopieren.
 *
 * Nachtrag 2: Unterpositionen lassen sich einklappen, eine Zeile „Abzug" zieht Prozent oder
 * Pauschale von der Zwischensumme darüber ab, und unter Menge und Einheit steht
 * die Alternativmenge.
 *
 * Nachtrag 8 (N-64/N-69 neu, 19.9.2026): Markiert wird am linken Rand
 * (Klick, Strg, Umschalt, Entlangfahren), ein Klick anderswohin hebt die
 * Markierung auf. Gegriffen wird die Zeile an ihrer freien Fläche; sie wandert
 * in ihrer Ebene, markierte Zeilen derselben Ebene wandern mit.
 *
 * Rechnen: live mit dem Rechenkern des Servers (`rechne`). Die Wahrheit ist
 * trotzdem die Antwort nach dem Speichern.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, Camera, ChevronDown, ChevronLeft, ChevronRight, Image as ImageIcon, Paperclip, Plus, Trash2, X } from 'lucide-react'
import {
  LINE_FILE_TYPES,
  PRICE_UNIT_MAX,
  istGueltigePreiseinheit,
  type DocumentLineType,
  type PriceMode,
  type TaxProcedure,
} from '@symphenia/shared-types'
import { RAND_ZEIGER, ZiehBereich, useRandMarkieren, useZeileGreifen, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { ZELLE_RAHMEN } from '@/components/zeilenblock'
import { KlappUmschalter, useKlappModus, zeileOffen } from '@/components/klappmodus'
import { PapierkorbFenster, RueckgaengigHinweis } from '@/components/Papierkorb'
import { GeldFeld } from '@/components/geldfeld'
import { documentsApi } from '../api/documents.api'
import {
  aendere,
  ausruecken,
  einfuegenNach,
  einruecken,
  entferneMitAblage,
  finde,
  flach,
  formatBetrag,
  leseZahl,
  neueZeile,
  neuesKind,
  rechne,
  stelleWiederHer,
  verschiebe,
  verschiebeVor,
  wartendeDateiId,
  zahlText,
  type GeloeschteZeile,
  type GridLine,
} from '../lib/grid'
import { LineImage, VorlaeufigesFoto } from './LineImage'
import type { ArticleRef, LineFileRef, ProjectPositionRef, ProjectRef } from '../types/document.types'

/** N-5: „Fotos anzeigen" ist eine Einstellung des Benutzers im Browser, nicht des Belegs. */
const FOTO_SCHLUESSEL = 'symphenia-beleg-fotos-anzeigen'

function fotosAnzeigenGespeichert(): boolean {
  try {
    return localStorage.getItem(FOTO_SCHLUESSEL) !== '0'
  } catch {
    return true
  }
}

const TYPEN: DocumentLineType[] = ['ARTICLE', 'TEXT', 'TITLE', 'SUBTOTAL', 'DEDUCTION', 'PAGE_BREAK']

/**
 * N-88: Die Eingabefelder tragen Rand und Fläche wie in der Projekt-Stückliste —
 * vorher waren sie durchsichtig, und wo etwas einzugeben war, zeigte erst der
 * Klick. Den Rahmen gibt `ZELLE_RAHMEN` vor; die Textfarbe bleibt hier frei,
 * weil eine Unterposition auch gesperrt grau bleiben soll.
 */
const zelle = `${ZELLE_RAHMEN} px-1.5 py-1 text-sm`
const klein = `${ZELLE_RAHMEN} px-1.5 py-0 text-xs text-muted-foreground`
/**
 * N-99: Luft zwischen einem Feld und dem darunter. Ohne sie legte sich der
 * Fokusrahmen des oberen Felds auf die Oberkante des unteren (Auftraggeber
 * 20.9.2026, am Langtext gesehen).
 */
const abstand = 'mt-1'

/** Nachtrag 4: welche Dateien die Auswahl anbietet — dieselbe Liste wie die Prüfung im Server. */
const DATEI_ENDUNGEN = Object.keys(LINE_FILE_TYPES)
  .map((e) => `.${e}`)
  .join(',')

function groesse(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** Menge × Verhältnis, auf drei Stellen — die Alternativmenge folgt der Menge. */
function alternativText(menge: string, verhaeltnis: number | null): string | null {
  const m = leseZahl(menge)
  if (verhaeltnis === null || m === null || Number.isNaN(m)) return null
  return zahlText(Math.round(m * verhaeltnis * 1000) / 1000, 3)
}

/**
 * Die Preiseinheit einer Zeile — eine ganze Zahl von 1 bis 1.000.000 (AP-28 N-12).
 *
 * **Sie rechnet beim Tippen mit** (Auftraggeber 7.10.2026): Sobald eine gültige Zahl dasteht, gilt sie, und der
 * Betrag daneben folgt — wie bei Menge, Preis und Rabatt. Bis dahin griff sie erst beim Verlassen des Felds.
 * Ein ungültiger Wert wird weiterhin nicht übernommen: Die Zeile rechnet mit dem letzten gültigen, und beim
 * Verlassen steht er wieder im Feld.
 *
 * Ein gewöhnliches Textfeld ohne Pfeile und ohne Vorschlagsliste (N-90, N-98): Die Pfeile eines
 * `type="number"` und der Pfeil, den Chrome an jedes Feld mit `list` hängt, nahmen der schmalen Spalte den
 * halben Platz.
 */
function PreiseinheitFeld({ wert, onWert, disabled, title, className }: { wert: number; onWert: (zahl: number) => void; disabled: boolean; title: string; className: string }) {
  /** Was gerade getippt wird — `null`, solange das Feld den Wert der Zeile zeigt. */
  const [text, setText] = useState<string | null>(null)
  return (
    <input
      type="text"
      inputMode="numeric"
      maxLength={String(PRICE_UNIT_MAX).length}
      disabled={disabled}
      title={title}
      className={className}
      value={text ?? String(wert)}
      onChange={(e) => {
        const getippt = e.target.value
        setText(getippt)
        const zahl = Number(getippt.trim())
        if (getippt.trim() && istGueltigePreiseinheit(zahl) && zahl !== wert) onWert(zahl)
      }}
      onBlur={() => setText(null)}
    />
  )
}

/** Eine Rasterzeile, die sich an ihrer freien Fläche greifen lässt (N-64 neu). */
function RasterZeile({ zeilenKey, ziehbar, className, children }: { zeilenKey: string; ziehbar: boolean; className: string; children: ReactNode }) {
  const greifen = useZeileGreifen(zeilenKey, !ziehbar)
  return (
    <tr ref={greifen.ref} {...greifen.listeners} data-key={zeilenKey} tabIndex={-1} className={`${className} ${greifen.listeners ? 'zeile-greifbar' : ''}`}>
      {children}
    </tr>
  )
}

interface Props {
  zeilen: GridLine[]
  onChange: (zeilen: GridLine[]) => void
  editierbar: boolean
  /** Feldrecht: ohne es existieren die Preisspalten nicht. */
  preise: boolean
  /** Die Art führt Preise (Preisanfrage nie). */
  preiseDerArt: boolean
  waehrung: string
  kopf: { priceMode: PriceMode; taxProcedure: TaxProcedure; documentDiscountPercent: number | null }
  vorgabeSatz: number
  projekteVerfuegbar: boolean
  /** Projekt und Position des Belegkopfs — eine neue Zeile erbt beides (N-101). */
  kopfProjekt: { projectId: string | null; projectNumber: string | null; projectPositionId: string | null; projectPositionNumber: string | null; projectPositionName: string | null }
  artikelRolle: 'sales' | 'purchase' | undefined
  /** Einkauf: der Lieferant des Belegs — nur dessen Preis und Artikelnummer (Phase G). */
  partnerId: string | null
  /** Sprache des Belegs — für den Hinweis auf die Artikelnummer des Lieferanten. */
  sprache: string
  auswahl: Set<string>
  onAuswahl: (auswahl: Set<string>) => void
  onCopy: () => void
  onPaste: (nachZeileKey: string | null) => void
  onFoto: (zeilenKey: string, datei: File) => void
  onFotoEntfernen: (zeilenKey: string) => void
  /** Für das Laden der Fotos (N-4). */
  documentId: string
  /** Nachtrag 3: Einheiten aus dem Artikelstamm als Vorschlag — freie Eingabe bleibt. */
  einheiten: { code: string; name: string }[]
  /** Nachtrag 4: Dateien an Positionen — hochladen, entfernen, herunterladen. */
  onDateien: (zeilenKey: string, dateien: File[]) => void
  onDateiEntfernen: (zeilenKey: string, fileId: string) => void
  onDateiLaden: (zeilenKey: string, datei: LineFileRef) => void
}

export function PositionGrid(props: Props) {
  const { t } = useTranslation()
  const { zeilen, onChange, editierbar, preise, preiseDerArt, waehrung, auswahl, onAuswahl } = props
  const zeigePreise = preise && preiseDerArt
  const [fokusNach, setFokusNach] = useState<string | null>(null)
  const [artikelSuche, setArtikelSuche] = useState<{ key: string; treffer: ArticleRef[] } | null>(null)
  const [projekte, setProjekte] = useState<ProjectRef[]>([])
  const [positionen, setPositionen] = useState<Record<string, ProjectPositionRef[]>>({})
  const [fotosAnzeigen, setFotosAnzeigen] = useState(fotosAnzeigenGespeichert)
  /** Nachtrag 2: eingeklappte Hauptpositionen — nur Anzeige, nichts am Beleg. */
  const [eingeklappt, setEingeklappt] = useState<Set<string>>(new Set())
  /**
   * Nachtrag 12 (N-93/N-94): Zeilen, deren weitere Angaben aufgeklappt sind.
   * **Zugeklappt heißt zugeklappt** (N-95): Auch eine gefüllte Zeile verbirgt
   * ihre Felder — dass sie etwas trägt, zeigt der Punkt am Schalter.
   */
  const [weitereOffen, setWeitereOffen] = useState<Set<string>>(new Set())
  /**
   * Auftraggeber 10.10.2026: ein Umschalter für die ganze Liste — die weiteren Angaben aller Zeilen immer
   * aufgeklappt, immer zugeklappt oder wie bisher je Zeile über „Weitere Angaben“ (`auto`). Je Benutzer gemerkt.
   */
  const [klappModus, setKlappModus] = useKlappModus('document_lines')
  const zeigtWeitere = (key: string) => zeileOffen(klappModus, weitereOffen.has(key))
  /**
   * N-102: Der Papierkorb der Maske. Er liegt im Browser, nicht auf dem Server —
   * die Positionen werden als Ganzes gespeichert, eine einzelne Zeile gibt es dort
   * erst nach dem Speichern. Er hält darum bis zum Verlassen des Belegs.
   */
  const [papierkorb, setPapierkorb] = useState<GeloeschteZeile[]>([])
  const [zuletztGeloescht, setZuletztGeloescht] = useState<GeloeschteZeile[]>([])
  const [papierkorbOffen, setPapierkorbOffen] = useState(false)
  const suchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tabelle = useRef<HTMLTableSectionElement>(null)

  const ergebnis = useMemo(() => rechne(zeilen, props.kopf, props.vorgabeSatz), [zeilen, props.kopf, props.vorgabeSatz])
  const nachKey = useMemo(() => new Map(ergebnis.lines.map((l) => [l.key, l])), [ergebnis])
  const zeilenFlach = useMemo(() => flach(zeilen), [zeilen])
  const sichtbar = zeilenFlach.filter(({ eltern }) => !eltern || !eingeklappt.has(eltern.key))
  const mitKindern = zeilen.filter((z) => z.children.length > 0)

  useEffect(() => {
    if (!props.projekteVerfuegbar) return
    documentsApi
      .projects('')
      .then(({ data }) => setProjekte(data.data))
      .catch(() => setProjekte([]))
  }, [props.projekteVerfuegbar])

  useEffect(() => {
    if (!fokusNach) return
    const feld = tabelle.current?.querySelector<HTMLElement>(`tr[data-key="${fokusNach}"] [data-feld="articleName"]`)
    feld?.focus()
    setFokusNach(null)
  }, [fokusNach, zeilen])

  const positionenLaden = (projectId: string | null) => {
    if (!projectId || positionen[projectId]) return
    documentsApi
      .projectPositions(projectId)
      .then(({ data }) => setPositionen((v) => ({ ...v, [projectId]: data.data })))
      .catch(() => undefined)
  }

  const setze = (key: string, patch: Partial<GridLine>) => onChange(aendere(zeilen, key, patch))

  /** Wie eine gelöschte Zeile im Hinweis und im Papierkorb heißt. */
  const zeilenName = (z: GridLine) => z.articleName.trim() || t(`documents.lineTypes.${z.lineType}`)
  /** Löschen heißt: aus dem Raster nehmen **und** in den Papierkorb legen. */
  const loesche = (keys: ReadonlySet<string>) => {
    const { zeilen: rest, geloescht } = entferneMitAblage(zeilen, keys, zeilenName)
    if (!geloescht.length) return
    onChange(rest)
    setPapierkorb((p) => [...p, ...geloescht])
    setZuletztGeloescht(geloescht)
  }
  const hole = (trashId: string) => {
    const eintrag = papierkorb.find((e) => e.trashId === trashId)
    if (!eintrag) return
    onChange(stelleWiederHer(zeilen, eintrag))
    setPapierkorb((p) => p.filter((e) => e.trashId !== trashId))
    setZuletztGeloescht((p) => p.filter((e) => e.trashId !== trashId))
  }

  const neueNach = (key: string | null, typ: DocumentLineType = 'ARTICLE') => {
    const neu = neueZeile(typ, props.kopfProjekt)
    onChange(einfuegenNach(zeilen, key, neu))
    setFokusNach(neu.key)
  }

  /** N-6: leere Unterposition als letzte der Gruppe — ohne Alt+→ zu kennen. */
  const neuesKindUnter = (elternKey: string) => {
    const neu = neueZeile('ARTICLE')
    onChange(neuesKind(zeilen, elternKey, neu))
    setEingeklappt((e) => {
      const neuSet = new Set(e)
      neuSet.delete(elternKey)
      return neuSet
    })
    setFokusNach(neu.key)
  }

  const schalteFotos = (an: boolean) => {
    setFotosAnzeigen(an)
    try {
      localStorage.setItem(FOTO_SCHLUESSEL, an ? '1' : '0')
    } catch {
      // Ohne Speicher gilt die Wahl nur bis zum Neuladen.
    }
  }
  const mitFotos = zeilenFlach.some(({ zeile }) => zeile.hasImage)

  const schalteGruppe = (key: string) =>
    setEingeklappt((e) => {
      const neu = new Set(e)
      if (neu.has(key)) neu.delete(key)
      else neu.add(key)
      return neu
    })
  const alleEingeklappt = mitKindern.length > 0 && mitKindern.every((z) => eingeklappt.has(z.key))

  /**
   * N-97: Die vier Zusatzfelder liegen **nicht** in der Aktivierreihenfolge
   * (`tabIndex={-1}`) — Tab führt durch die Zeile, nicht durch den Ausnahmefall.
   * Steht der Fokus doch in einem, führt Tab dorthin, wo der Auftraggeber ihn
   * erwartet: aus Auftragsnummer und Positionsbeschreibung zur **Menge derselben
   * Position**, vom Liefertermin zu seiner **Anmerkung**, von dort in die
   * **nächste Position**.
   */
  const zeileImRaster = (key: string) => tabelle.current?.querySelector<HTMLElement>(`tr[data-key="${CSS.escape(key)}"]`)
  const mengeVon = (key: string) => zeileImRaster(key)?.querySelector<HTMLElement>('[data-feld="quantity"]') ?? null
  const anmerkungVon = (key: string) => {
    const zeile = zeileImRaster(key)?.nextElementSibling
    return zeile?.hasAttribute('data-key') === false ? zeile.querySelector<HTMLElement>('[data-feld="deliveryNote"]') ?? null : null
  }
  const mengeDerNaechsten = (key: string) => {
    const alle = [...(tabelle.current?.querySelectorAll<HTMLElement>('tr[data-key]') ?? [])]
    const i = alle.findIndex((r) => r.dataset.key === key)
    return (i >= 0 ? alle[i + 1] : null)?.querySelector<HTMLElement>('[data-feld="quantity"]') ?? null
  }
  /** Tab (ohne Umschalt) aus einem Zusatzfeld auf ein bestimmtes Ziel lenken. */
  const zusatzWeiter = (ziel: () => HTMLElement | null) => (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || e.shiftKey || e.altKey || e.ctrlKey) return
    const el = ziel()
    if (!el) return
    e.preventDefault()
    e.stopPropagation()
    el.focus()
  }

  /** Trägt die Zeile eine Positionsbeschreibung oder eine Abweichung vom Beleg? */
  const hatWeitere = (z: GridLine) => Boolean(z.introText || z.customerReference || z.deliveryDate || z.deliveryNote)
  const schalteWeitere = (key: string) =>
    setWeitereOffen((e) => {
      const neu = new Set(e)
      if (neu.has(key)) neu.delete(key)
      else neu.add(key)
      return neu
    })

  const sucheArtikel = (key: string, text: string) => {
    setze(key, { articleNumber: text, articleId: null })
    if (suchTimer.current) clearTimeout(suchTimer.current)
    if (!text.trim()) {
      setArtikelSuche(null)
      return
    }
    suchTimer.current = setTimeout(() => {
      documentsApi
        // Der Partner geht in beiden Richtungen mit: Einkauf → Lieferantenpreis, Verkauf → Kundenpreis (AP-28 N-10).
        .articles(text, props.artikelRolle, props.partnerId)
        .then(({ data }) => setArtikelSuche({ key, treffer: data.data }))
        .catch(() => setArtikelSuche(null))
    }, 250)
  }

  const waehleArtikel = (z: GridLine, a: ArticleRef) => {
    const preis = a.price !== undefined ? a.price : a.salesPrice
    // Ein Lieferantenpreis in fremder Währung wird nicht übernommen — derselbe Satz wie im Server (19.2-43).
    const preisGilt = preis !== undefined && preis !== null && (!a.priceCurrency || a.priceCurrency === waehrung)
    // Wie `lieferantenNummerText` im Backend: die Artikelnummer des Lieferanten als erste Zeile des Langtexts.
    const lieferantenNr = a.supplierArticleNumber?.trim()
      ? props.sprache === 'en'
        ? `Your item no. ${a.supplierArticleNumber.trim()}`
        : `Ihre Art.-Nr. ${a.supplierArticleNumber.trim()}`
      : null
    // Nachtrag 2: Die erste Alternativeinheit des Artikels schlägt die Alternativmenge vor.
    const alt = a.altUnits?.find((u) => u.baseQuantity)
    const verhaeltnis = alt ? alt.quantity / alt.baseQuantity : null
    setze(z.key, {
      articleId: a.id,
      articleNumber: a.articleNumber,
      articleName: a.name,
      unitCode: a.unitCode,
      description: [lieferantenNr, a.documentText].filter(Boolean).join('\n'),
      // Preis **und** Preiseinheit gemeinsam (4.4.1).
      ...(preisGilt && { unitPrice: zahlText(preis, 2), priceUnit: a.priceUnit }),
      taxRate: a.taxRate === null ? '' : zahlText(a.taxRate, 2),
      altUnitCode: alt?.unitCode ?? '',
      altVerhaeltnis: verhaeltnis,
      altQuantity: alternativText(z.quantity, verhaeltnis) ?? '',
    })
    setArtikelSuche(null)
  }

  /**
   * Die Felder, die die Tabulatortaste anläuft — die Nebenfelder (Alternativmenge,
   * Alternativeinheit, Zwischensummen-Modus) tragen `tabIndex={-1}` und bleiben außen vor.
   */
  const bedienbar = (bereich: Element) =>
    [...bereich.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input:not([type="checkbox"]), textarea, select',
    )].filter((f) => !f.disabled && f.tabIndex >= 0 && !('readOnly' in f && f.readOnly))

  /**
   * Nachtrag 11: Enter **legt keine Zeile an** — es verlässt das Feld wie ein
   * Klick daneben und setzt den Fokus in dieselbe Spalte der nächsten sichtbaren
   * Zeile (AP-36 3.2). Gesucht wird über die Tabellenzelle, nicht über die
   * laufende Nummer des Feldes: Eine Hauptposition trägt andere Felder als ihre
   * Unterposition, die Spalten stehen aber übereinander. Fehlt dort ein Feld,
   * greift das erste der Zeile; in der letzten Zeile gibt das Feld den Fokus ab.
   * Neue Zeilen entstehen über die Schalter „+ Position" usw. unter dem Raster.
   */
  const naechsteZeileFokussieren = (ziel: HTMLElement, key: string) => {
    const feld = ziel.closest<HTMLElement>('input:not([type="checkbox"]), textarea, select')
    const zelle = feld?.closest('td')
    const zeile = zelle?.closest('tr')
    const spalte = zeile && zelle ? [...zeile.children].indexOf(zelle) : -1
    const platz = zelle && feld ? bedienbar(zelle).indexOf(feld as HTMLInputElement) : -1

    const i = sichtbar.findIndex(({ zeile: z }) => z.key === key)
    const naechsterKey = i >= 0 ? sichtbar[i + 1]?.zeile.key : undefined
    const naechste = naechsterKey ? tabelle.current?.querySelector(`tr[data-key="${naechsterKey}"]`) : null
    if (!naechste) {
      feld?.blur()
      return
    }
    const inZelle = spalte >= 0 ? bedienbar(naechste.children[spalte] ?? naechste) : []
    const zielFeld = inZelle[platz] ?? inZelle[0] ?? bedienbar(naechste)[0]
    if (zielFeld) zielFeld.focus()
    else feld?.blur()
  }

  const tasten = (e: React.KeyboardEvent) => {
    const ziel = e.target as HTMLElement
    const imFeld = !!ziel.closest('input:not([type="checkbox"]), textarea, select')
    const key = ziel.closest('tr')?.dataset.key ?? null

    if (e.altKey && key && editierbar && ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault()
      if (e.key === 'ArrowRight') onChange(einruecken(zeilen, key))
      if (e.key === 'ArrowLeft') onChange(ausruecken(zeilen, key))
      if (e.key === 'ArrowUp') onChange(verschiebe(zeilen, key, -1))
      if (e.key === 'ArrowDown') onChange(verschiebe(zeilen, key, 1))
      return
    }
    if (e.key === 'Enter' && imFeld && ziel.tagName !== 'TEXTAREA' && key && !e.nativeEvent.isComposing) {
      e.preventDefault()
      naechsteZeileFokussieren(ziel, key)
      return
    }
    if (e.key === 'Escape' && imFeld && key) {
      e.preventDefault()
      ;(ziel as HTMLInputElement).blur()
      onAuswahl(new Set([key]))
      tabelle.current?.querySelector<HTMLElement>(`tr[data-key="${key}"]`)?.focus()
      return
    }
    // Fokusregel: Strg+C/V gehören den Zeilen nur, wenn kein Feld bearbeitet wird.
    if (!imFeld && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && auswahl.size) {
      e.preventDefault()
      props.onCopy()
      return
    }
    if (!imFeld && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v' && editierbar) {
      e.preventDefault()
      props.onPaste([...auswahl].at(-1) ?? null)
      return
    }
    if (!imFeld && e.key === 'Delete' && auswahl.size && editierbar) {
      e.preventDefault()
      loesche(auswahl)
      onAuswahl(new Set())
    }
  }

  const alleMarkiert = zeilenFlach.length > 0 && zeilenFlach.every(({ zeile }) => auswahl.has(zeile.key))

  // N-64 neu: Markieren am linken Rand, Aufheben per Klick anderswohin.
  const reihe = useMemo(() => sichtbar.map(({ zeile }) => zeile.key), [sichtbar])
  // Strg+C auch dann, wenn der Fokus außerhalb des Rasters liegt (etwa nach dem Markieren im Kopf).
  const { amRand } = useRandMarkieren({ behaelter: tabelle, reihe, markiert: auswahl, setMarkiert: onAuswahl, onKopieren: props.onCopy })

  // N-66: Blöcke der Ebene — eine Hauptposition samt sichtbaren Unterpositionen, eine Unterposition allein in ihrer Gruppe.
  const bloeckeFuer = useCallback(
    (key: string) => {
      const f = finde(zeilen, key)
      if (!f) return []
      if (f.eltern) return f.eltern.children.map((k) => ({ key: k.key, zeilen: [k.key] }))
      return zeilen.map((z) => ({ key: z.key, zeilen: [z.key, ...(eingeklappt.has(z.key) ? [] : z.children.map((k) => k.key))] }))
    },
    [zeilen, eingeklappt],
  )
  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter: tabelle,
    bloeckeFuer,
    onAblegen: (key, vorKey) => {
      const neu = verschiebeVor(zeilen, key, [...auswahl], vorKey)
      if (neu !== zeilen) onChange(neu)
    },
  })
  /** N-69 neu: Was mit der gegriffenen Zeile wandert — die Markierten ihrer Ebene, Hauptpositionen samt Kindern. */
  const wandernd = useMemo(() => {
    const f = gezogen ? finde(zeilen, gezogen) : null
    if (!f) return []
    const ebene = f.eltern ? f.eltern.children : zeilen
    return auswahl.has(f.zeile.key) ? ebene.filter((z) => auswahl.has(z.key)) : [f.zeile]
  }, [gezogen, zeilen, auswahl])
  const abgeblendet = new Set(wandernd.flatMap((z) => [z.key, ...z.children.map((k) => k.key)]))
  // Die Linie steht an der Oberkante des Zielblocks oder an der Unterkante seiner letzten sichtbaren Zeile.
  const linie = (() => {
    if (!ziel) return null
    if (ziel.kante === 'oben') return { key: ziel.key, kante: 'oben' as const }
    const block = bloeckeFuer(gezogen ?? ziel.key).find((b) => b.key === ziel.key)
    return { key: block?.zeilen[block.zeilen.length - 1] ?? ziel.key, kante: 'unten' as const }
  })()
  const gezogeneZeile = wandernd.find((z) => z.key === gezogen)
  const gezogenNr = gezogeneZeile ? nachKey.get(gezogeneZeile.key) : undefined

  // Kästchen · Pos · Typ · Menge · Einheit · Bezeichnung · Artikel · Preise (5) · Projekt (2)
  const spalten = 7 + (zeigePreise ? 5 : 0) + (props.projekteVerfuegbar ? 2 : 0)
  /** Mit der Spalte der Zeilenschalter ganz rechts. */
  const spaltenGesamt = spalten + 1
  /**
   * N-95: Die Zusatzfelder stehen **im Raster**, nicht in der Bezeichnungsspalte
   * (Auftraggeber 20.9.2026). Zeile davor: Auftragsnummer über Menge und Einheit,
   * Positionsbeschreibung über Bezeichnung und Artikel.
   *
   * N-97: Liefertermin und Anmerkung stehen **neben dem Langtext**, nicht unter der
   * ganzen Zeile: Die sechs linken Zellen der Positionszeile reichen über beide
   * Zeilen (`rowSpan`), die zweite Zeile beginnt darum an der Artikelspalte und
   * reicht bis zum Ende des Rabattfelds. Ohne Preisspalten (Preisanfrage) gibt es
   * kein Rabattfeld; dann füllt sie den Rest bis zur Schalterspalte.
   */
  const zusatzOben = { leerVorn: 3, auftrag: 2, beschreibung: 2, rest: spaltenGesamt - 7 }
  const zusatzUnten = (() => {
    const breite = zeigePreise ? 4 : Math.max(1, spaltenGesamt - 7)
    return { termin: breite, rest: spaltenGesamt - 6 - breite }
  })()

  return (
    // N-92: Die Liste steht auf der Kartenfläche, die Felder auf der helleren
    // Hintergrundfläche — dieselbe Staffelung wie die Stückliste. Ohne sie hätten
    // Feld und Untergrund dieselbe Farbe, und nur der Rand zeigte das Feld.
    <div className="overflow-x-auto rounded-md border border-border bg-card" onKeyDown={tasten}>
      {
        <div className="relative flex flex-wrap items-center justify-end gap-4 border-b border-border bg-muted/20 px-2 py-1 text-xs">
          {/* N-102: „… gelöscht. [Rückgängig]" steht links, der Papierkorb rechts. */}
          {zuletztGeloescht.length > 0 && (
            <span className="mr-auto">
              <RueckgaengigHinweis
                geloescht={zuletztGeloescht}
                onRueckgaengig={hole}
                onSchliessen={() => setZuletztGeloescht([])}
                texte={{
                  satz: (name, anzahl) => t('documents.grid.deletedRow', { name, count: anzahl }),
                  rueckgaengig: t('documents.grid.undo'),
                  schliessen: t('common.close'),
                }}
              />
            </span>
          )}
          {editierbar && (
            <button
              type="button"
              data-auswahl-behalten
              aria-expanded={papierkorbOffen}
              onClick={() => setPapierkorbOffen((o) => !o)}
              className={`inline-flex items-center gap-1 ${papierkorb.length ? 'text-foreground' : 'text-muted-foreground'} hover:text-foreground`}
              title={t('documents.grid.trashHint')}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('documents.grid.trash')}
              {papierkorb.length > 0 && <span className="rounded-full bg-primary px-1.5 text-[10px] text-primary-foreground">{papierkorb.length}</span>}
            </button>
          )}
          {papierkorbOffen && (
            <PapierkorbFenster
              titel={t('documents.grid.trashTitle')}
              aufbewahrung={t('documents.grid.trashKeeps')}
              laden={async () => papierkorb.map((e) => ({ id: e.trashId, name: e.name, deletedAt: e.geloeschtAm, deletedByName: null }))}
              onWiederherstellen={hole}
              onSchliessen={() => setPapierkorbOffen(false)}
              texte={{
                titel: t('documents.grid.trash'),
                leer: t('documents.grid.trashEmpty'),
                wiederherstellen: t('documents.grid.restore'),
                geloescht: (tag) => t('documents.grid.deletedOn', { date: tag }),
              }}
            />
          )}
          {mitKindern.length > 0 && (
            <button
              type="button"
              onClick={() => setEingeklappt(alleEingeklappt ? new Set() : new Set(mitKindern.map((z) => z.key)))}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              {alleEingeklappt ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {t(alleEingeklappt ? 'documents.grid.expandAll' : 'documents.grid.collapseAll')}
            </button>
          )}
          {mitFotos && (
            <label className="flex items-center gap-1.5 text-muted-foreground">
              <input type="checkbox" checked={fotosAnzeigen} onChange={(e) => schalteFotos(e.target.checked)} />
              <ImageIcon className="h-3.5 w-3.5" />
              {t('documents.grid.showPhotos')}
            </label>
          )}
          <KlappUmschalter
            modus={klappModus}
            onModus={setKlappModus}
            texte={{
              gruppe: t('documents.grid.fold.group'),
              auf: { name: t('documents.grid.fold.open'), titel: t('documents.grid.fold.openHint') },
              auto: { name: t('documents.grid.fold.auto'), titel: t('documents.grid.fold.autoHint') },
              zu: { name: t('documents.grid.fold.closed'), titel: t('documents.grid.fold.closedHint') },
            }}
          />
        </div>
      }
      <datalist id="einheit-vorschlaege">
        {props.einheiten.map((e) => (
          <option key={e.code} value={e.code}>
            {e.name}
          </option>
        ))}
      </datalist>
      <ZiehBereich
        dndProps={dndProps}
        vorschau={
          gezogeneZeile && (
            <>
              {wandernd.length > 1 && <span className="mr-1.5 rounded bg-primary px-1.5 text-xs text-primary-foreground">{t('documents.grid.dragRows', { count: wandernd.length })}</span>}
              {gezogenNr?.positionNo ? <span className="mr-1.5 font-mono text-xs text-muted-foreground">{gezogenNr.subPositionNo ? `${gezogenNr.positionNo}.${gezogenNr.subPositionNo}` : gezogenNr.positionNo}</span> : null}
              <span className="font-medium">{gezogeneZeile.articleName || t(`documents.lineTypes.${gezogeneZeile.lineType}`)}</span>
              {gezogeneZeile.children.length > 0 && <span className="ml-1.5 text-xs text-muted-foreground">{t('documents.grid.dragChildren', { count: gezogeneZeile.children.length })}</span>}
            </>
          )
        }
      >
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/50 text-xs text-muted-foreground">
          {/* N-90: Die Beschriftungen stehen senkrecht mittig — eine umbrechende
              Überschrift verschöbe sonst die Nachbarn nach unten. */}
          <tr className="h-8 align-middle [&>th]:whitespace-nowrap">
            <th className="relative w-2 p-0">
              {/* Der Rand im Kopf markiert alle Zeilen — oder keine mehr. */}
              <div
                data-rand
                role="checkbox"
                aria-checked={alleMarkiert}
                aria-label={t('documents.grid.selectAll')}
                title={t('documents.grid.selectAll')}
                className={`absolute inset-0 ${alleMarkiert ? 'bg-primary' : 'hover:bg-primary/30'}`}
                style={{ cursor: RAND_ZEIGER }}
                onMouseDown={(e) => {
                  if (e.button !== 0) return
                  e.preventDefault()
                  onAuswahl(alleMarkiert ? new Set() : new Set(zeilenFlach.map(({ zeile }) => zeile.key)))
                }}
              />
            </th>
            <th className="w-14 px-1.5 text-left">{t('documents.grid.pos')}</th>
            <th className="w-24 px-1.5 text-left">{t('documents.grid.type')}</th>
            <th className="w-20 px-1.5 text-right">{t('documents.grid.quantity')}</th>
            <th className="w-16 px-1.5 text-left">{t('documents.grid.unit')}</th>
            <th className="min-w-[16rem] px-1.5 text-left">{t('documents.grid.name')}</th>
            <th className="w-28 px-1.5 text-left">{t('documents.grid.articleNumber')}</th>
            {zeigePreise && (
              <>
                <th className="w-24 px-1.5 text-right">{t('documents.grid.unitPrice')}</th>
                {/* Nachtrag 2: Die Preiseinheit steht in jeder Zeile — wie am Artikel. */}
                <th className="w-20 px-1.5 text-right">{t('documents.grid.priceUnit')}</th>
                <th className="w-16 px-1.5 text-right">{t('documents.grid.discount')}</th>
                <th className="w-28 px-2 text-right">{t('documents.grid.amount')}</th>
                {/* Nachtrag 3: USt % rechts hinter dem Betrag. */}
                <th className="w-16 px-1.5 text-right">{t('documents.grid.taxRate')}</th>
              </>
            )}
            {props.projekteVerfuegbar && (
              <>
                <th className="w-32 px-1.5 text-left">{t('documents.grid.project')}</th>
                <th className="w-40 px-1.5 text-left">{t('documents.grid.projectPosition')}</th>
              </>
            )}
            <th className="w-28 px-1.5" />
          </tr>
        </thead>
        <tbody ref={tabelle}>
          {sichtbar.map(({ zeile: z, tiefe, eltern }) => {
            const r = nachKey.get(z.key)
            const markiert = auswahl.has(z.key)
            const nr = r && (z.lineType === 'ARTICLE' || z.lineType === 'REFERENCE') ? (r.subPositionNo ? `${r.positionNo}.${r.subPositionNo}` : String(r.positionNo)) : ''
            const summeDerKinder = z.children.length > 0 && (z.subMode ?? 'SUM_OF_CHILDREN') === 'SUM_OF_CHILDREN'
            const textKind = eltern?.subMode === 'OWN_PRICE'
            const preisGesperrt = !editierbar || summeDerKinder || textKind
            const mitWerten = z.lineType === 'ARTICLE'
            const abzug = z.lineType === 'DEDUCTION'
            const abzugProzent = abzug && (z.discountPercent !== '' || z.unitPrice === '')
            // Nachtrag 2: Unterpositionen stehen ganz grau — die Hauptposition trägt den Betrag schwarz.
            const grau = tiefe === 1 ? 'text-muted-foreground' : ''
            const preisFeld = mitWerten && !summeDerKinder && !textKind
            const zusatzOffen = mitWerten && zeigtWeitere(z.key)
            /** Die linken Zellen reichen über die Positionszeile und die Zeile darunter (N-97). */
            const zusatzZeilen = zusatzOffen ? 2 : undefined
            // Die drei Zeilen sind ein Datensatz: die Trennlinie steht erst unter der letzten.
            const tönung = markiert ? 'bg-primary/15' : tiefe ? 'bg-muted/10' : ''
            const abgeblendetHier = abgeblendet.has(z.key) ? 'opacity-40' : ''
            /** Eine Beschriftung über einem Zusatzfeld — sie steht im Raster, nicht im Kopf. */
            const zusatzLabel = (text: string) => <span className="block truncate text-[11px] text-muted-foreground">{text}</span>
            return (
              <Fragment key={z.key}>
                {zusatzOffen && (
                  // N-95 Zeile davor: Auftragsnummer über Menge und Einheit,
                  // Positionsbeschreibung über Bezeichnung und Artikelnummer.
                  <tr className={`align-bottom ${tönung} ${abgeblendetHier}`}>
                    <td colSpan={zusatzOben.leerVorn} />
                    <td colSpan={zusatzOben.auftrag} className="px-1.5 pt-1">
                      <label className="block" title={t('documents.grid.lineDeviationHint')}>
                        {zusatzLabel(t('documents.grid.lineOrderNumber'))}
                        <input
                          disabled={!editierbar}
                          tabIndex={-1}
                          value={z.customerReference}
                          onChange={(e) => setze(z.key, { customerReference: e.target.value })}
                          onKeyDown={zusatzWeiter(() => mengeVon(z.key))}
                          className={`${zelle} text-xs`}
                          maxLength={100}
                        />
                      </label>
                    </td>
                    <td colSpan={zusatzOben.beschreibung} className="px-1.5 pt-1">
                      <label className="block">
                        {zusatzLabel(t('documents.grid.introText'))}
                        <textarea
                          disabled={!editierbar}
                          tabIndex={-1}
                          value={z.introText}
                          onChange={(e) => setze(z.key, { introText: e.target.value })}
                          onKeyDown={zusatzWeiter(() => mengeVon(z.key))}
                          // N-96: eine Zeile hoch wie das Feld daneben (`text-xs`: 1rem
                          // Zeilenhöhe, dazu `py-1` und der Rand) — größer ziehen geht weiter.
                          className={`${zelle} block h-[calc(1.5rem+2px)] resize-y text-xs leading-4`}
                          title={t('documents.grid.introTextHint')}
                        />
                      </label>
                    </td>
                    <td colSpan={zusatzOben.rest} />
                  </tr>
                )}
                <RasterZeile
                  zeilenKey={z.key}
                  ziehbar={editierbar}
                  className={`${zusatzOffen ? '' : 'border-b border-border last:border-0'} align-top focus:outline-none ${tönung} ${abgeblendetHier} ${
                    // N-67: die Linie als Innenschatten an den Zellen — ein Schatten am <tr> zeichnet nicht jeder Browser.
                    linie?.key === z.key ? (linie.kante === 'oben' ? '[&>td]:shadow-[inset_0_2px_0_hsl(var(--primary))]' : '[&>td]:shadow-[inset_0_-2px_0_hsl(var(--primary))]') : ''
                  }`}
                >
                  <td className="relative w-2 p-0" rowSpan={zusatzZeilen}>
                    <div
                      data-rand
                      role="checkbox"
                      aria-checked={markiert}
                      aria-label={t('documents.grid.selectRow')}
                      title={t('documents.grid.markEdge')}
                      className={`absolute inset-0 ${markiert ? 'bg-primary' : 'hover:bg-primary/30'}`}
                      style={{ cursor: RAND_ZEIGER }}
                      onMouseDown={amRand(z.key)}
                    />
                  </td>
                  <td className="px-1.5 py-1 font-mono text-xs text-muted-foreground" rowSpan={zusatzZeilen}>
                    <span className="inline-flex items-center gap-0.5">
                      {tiefe === 0 && z.children.length > 0 ? (
                        <button type="button" onClick={() => schalteGruppe(z.key)} className="hover:text-foreground" title={t('documents.grid.toggleChildren')} aria-label={t('documents.grid.toggleChildren')}>
                          {eingeklappt.has(z.key) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                      ) : (
                        <span className="inline-block w-3.5" />
                      )}
                      {nr || '·'}
                    </span>
                  </td>
                  <td className="px-1.5 py-1" rowSpan={zusatzZeilen}>
                    <select
                      disabled={!editierbar || tiefe === 1}
                      value={z.lineType}
                      onChange={(e) => {
                        const typ = e.target.value as DocumentLineType
                        // Ein Abzug beginnt prozentual: leeren Preis, Prozentsatz 0.
                        setze(z.key, typ === 'DEDUCTION' ? { lineType: typ, unitPrice: '', discountPercent: '0' } : { lineType: typ })
                      }}
                      className={zelle}
                    >
                      {TYPEN.filter((typ) => typ !== 'DEDUCTION' || zeigePreise || z.lineType === 'DEDUCTION').map((typ) => (
                        <option key={typ} value={typ}>
                          {t(`documents.lineTypes.${typ}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-1.5 py-1" rowSpan={zusatzZeilen}>
                    {mitWerten && (
                      <>
                        <input
                          data-feld="quantity"
                          disabled={!editierbar}
                          value={z.quantity}
                          onChange={(e) => {
                            const alt = alternativText(e.target.value, z.altVerhaeltnis)
                            setze(z.key, { quantity: e.target.value, ...(alt !== null && { altQuantity: alt }) })
                          }}
                          className={`${zelle} text-right tabular-nums ${grau}`}
                          inputMode="decimal"
                        />
                        {/* Nachtrag 2: Alternativmenge — Angabe, kein Rechenweg. */}
                        <input
                          disabled={!editierbar}
                          // Nachtrag 3: Tab springt von der Menge zur Einheit, nicht in die Alternativmenge.
                          tabIndex={-1}
                          value={z.altQuantity}
                          onChange={(e) => {
                            const alt = leseZahl(e.target.value)
                            const menge = leseZahl(z.quantity)
                            const verhaeltnis = alt !== null && !Number.isNaN(alt) && menge ? alt / menge : null
                            setze(z.key, { altQuantity: e.target.value, altVerhaeltnis: verhaeltnis })
                          }}
                          className={`${klein} ${abstand} text-right tabular-nums`}
                          inputMode="decimal"
                          placeholder={t('documents.grid.altQuantity')}
                          // N-78: Der Tipp schreibt aus, was der Platzhalter abkürzt.
                          title={t('documents.grid.altQuantityHint')}
                        />
                      </>
                    )}
                  </td>
                  <td className="px-1.5 py-1" rowSpan={zusatzZeilen}>
                    {mitWerten && (
                      <>
                        {/* N-100: eine echte Auswahl wie im Kopf — die Einheiten kommen aus dem
                            Artikelstamm. Eine Zeile, die eine fremde Einheit trägt (früher frei
                            eingegeben oder aus einem alten Beleg), behält sie als eigenen Eintrag. */}
                        <select disabled={!editierbar} value={z.unitCode} onChange={(e) => setze(z.key, { unitCode: e.target.value })} className={`${zelle} ${grau}`}>
                          <option value="">—</option>
                          {z.unitCode && !props.einheiten.some((e) => e.code === z.unitCode) && <option value={z.unitCode}>{z.unitCode}</option>}
                          {props.einheiten.map((e) => (
                            <option key={e.code} value={e.code}>
                              {e.code}
                            </option>
                          ))}
                        </select>
                        <input
                          disabled={!editierbar}
                          tabIndex={-1}
                          list="einheit-vorschlaege"
                          value={z.altUnitCode}
                          onChange={(e) => setze(z.key, { altUnitCode: e.target.value })}
                          className={`${klein} ${abstand}`}
                          placeholder={t('documents.grid.altUnit')}
                          title={t('documents.grid.altUnitHint')}
                          maxLength={20}
                        />
                      </>
                    )}
                  </td>
                  <td className="px-1.5 py-1" style={{ paddingLeft: tiefe ? '1.5rem' : undefined }} rowSpan={zusatzZeilen}>
                    <input
                      data-feld="articleName"
                      disabled={!editierbar || z.lineType === 'PAGE_BREAK'}
                      value={z.lineType === 'PAGE_BREAK' ? t('documents.lineTypes.PAGE_BREAK') : z.articleName}
                      onChange={(e) => setze(z.key, { articleName: e.target.value })}
                      // Nachtrag 2: Die Bezeichnung einer Hauptposition steht fett.
                      className={`${zelle} ${tiefe === 0 && z.lineType !== 'PAGE_BREAK' ? 'font-semibold' : ''} ${grau}`}
                      placeholder={z.lineType === 'SUBTOTAL' || abzug ? t(`documents.lineTypes.${z.lineType}`) : ''}
                    />
                    {(z.lineType === 'ARTICLE' || z.lineType === 'TEXT') && (
                      <textarea
                        disabled={!editierbar}
                        value={z.description}
                        onChange={(e) => setze(z.key, { description: e.target.value })}
                        // N-77: 2,5 Zeilen hoch (Zeilenhöhe 1rem, dazu Innenabstand und Rand) — die
                        // angeschnittene halbe Zeile zeigt, dass Text folgt; größer ziehen geht weiter.
                        className={`${zelle} ${abstand} block h-[calc(2.5rem+6px)] resize-y text-xs text-muted-foreground`}
                        placeholder={t('documents.grid.description')}
                      />
                    )}
                    {abzug && (
                      <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {zeigePreise && (
                          <select
                            disabled={!editierbar}
                            value={abzugProzent ? 'percent' : 'flat'}
                            onChange={(e) =>
                              setze(z.key, e.target.value === 'percent' ? { discountPercent: '0', unitPrice: '' } : { discountPercent: '', unitPrice: '0' })
                            }
                            className="rounded border border-input bg-background px-1 text-xs"
                          >
                            <option value="percent">{t('documents.grid.deductionPercent')}</option>
                            <option value="flat">{t('documents.grid.deductionFlat')}</option>
                          </select>
                        )}
                        {zeigePreise && r?.deductionBase !== null && r?.deductionBase !== undefined && (
                          <span>{t('documents.grid.deductionBase', { amount: formatBetrag(r.deductionBase, waehrung) })}</span>
                        )}
                      </div>
                    )}
                    {z.fotoNeu && (
                      // N-104: wartet auf „Speichern" — Vorschau aus der Datei im Browser.
                      <span className="relative mt-1 inline-block">
                        <VorlaeufigesFoto datei={z.fotoNeu} className="max-h-24 rounded border border-dashed border-amber-500" />
                        <span className="block text-xs text-amber-600">{t('documents.grid.pendingUntilSave')}</span>
                        {editierbar && (
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => props.onFotoEntfernen(z.key)}
                            className="absolute -right-2 -top-2 rounded-full border border-border bg-background p-0.5 text-muted-foreground shadow-sm hover:text-destructive"
                            title={t('documents.grid.photoRemove')}
                            aria-label={t('documents.grid.photoRemove')}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    )}
                    {!z.fotoNeu && !z.id && z.fotoAusAblage && (
                      <span className="mt-1 inline-flex items-center gap-1 text-xs text-amber-600">
                        <ImageIcon className="h-3.5 w-3.5" />
                        {t('documents.grid.photoFromClipboard')}
                      </span>
                    )}
                    {!z.fotoNeu && z.hasImage && z.id && z.fotoWeg && (
                      <span className="mt-1 inline-flex items-center gap-1 text-xs text-amber-600 line-through">
                        <ImageIcon className="h-3.5 w-3.5" />
                        {t('documents.grid.photoRemovedOnSave')}
                      </span>
                    )}
                    {!z.fotoNeu && !z.fotoWeg && z.hasImage && z.id &&
                      (fotosAnzeigen ? (
                        // Nachtrag 5: Entfernen sitzt an der rechten oberen Ecke des Fotos.
                        <span className="relative mt-1 inline-block">
                          <LineImage documentId={props.documentId} lineId={z.id} version={z.imageVersion} className="max-h-24 rounded border border-border" />
                          {editierbar && (
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => props.onFotoEntfernen(z.key)}
                              className="absolute -right-2 -top-2 rounded-full border border-border bg-background p-0.5 text-muted-foreground shadow-sm hover:text-destructive"
                              title={t('documents.grid.photoRemove')}
                              aria-label={t('documents.grid.photoRemove')}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      ) : (
                        <span className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <ImageIcon className="h-3.5 w-3.5" />
                          {t('documents.grid.hasPhoto')}
                          {editierbar && (
                            <button type="button" tabIndex={-1} onClick={() => props.onFotoEntfernen(z.key)} className="hover:text-destructive" title={t('documents.grid.photoRemove')} aria-label={t('documents.grid.photoRemove')}>
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    {(z.files.length > 0 || !!z.dateienNeu?.length) && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {(z.dateienNeu ?? []).map((datei, i) => (
                          // N-104: wartet auf „Speichern".
                          <span
                            key={wartendeDateiId(i)}
                            className="inline-flex items-center gap-1 rounded border border-dashed border-amber-500 px-1.5 py-0.5 text-xs text-amber-700"
                            title={t('documents.grid.pendingUntilSave')}
                          >
                            <Paperclip className="h-3 w-3" />
                            {datei.name}
                            <span className="text-muted-foreground">({groesse(datei.size)})</span>
                            {editierbar && (
                              <button type="button" tabIndex={-1} onClick={() => props.onDateiEntfernen(z.key, wartendeDateiId(i))} className="hover:text-destructive" title={t('documents.grid.fileRemove')} aria-label={t('documents.grid.fileRemove')}>
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        ))}
                        {z.files.filter((d) => !z.dateienWeg?.includes(d.id)).map((d) => (
                          <span key={d.id} className="inline-flex items-center gap-1 rounded border border-border bg-muted/30 px-1.5 py-0.5 text-xs">
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => props.onDateiLaden(z.key, d)}
                              className="inline-flex items-center gap-1 hover:text-primary hover:underline"
                              title={t('documents.grid.fileDownload')}
                            >
                              <Paperclip className="h-3 w-3" />
                              {d.fileName}
                              <span className="text-muted-foreground">({groesse(d.sizeBytes)})</span>
                            </button>
                            {editierbar && (
                              <button type="button" tabIndex={-1} onClick={() => props.onDateiEntfernen(z.key, d.id)} className="hover:text-destructive" title={t('documents.grid.fileRemove')} aria-label={t('documents.grid.fileRemove')}>
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    {mitWerten && (
                      <div className="mt-0.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {editierbar && tiefe === 0 && (
                          <button type="button" tabIndex={-1} onClick={() => neuesKindUnter(z.key)} className="inline-flex items-center gap-0.5 text-primary hover:underline" title={t('documents.grid.addChildTitle')}>
                            <Plus className="h-3 w-3" />
                            {t('documents.grid.addChild')}
                          </button>
                        )}
                        <label className="flex items-center gap-1">
                          <input type="checkbox" disabled={!editierbar} tabIndex={-1} checked={z.isOptional} onChange={(e) => setze(z.key, { isOptional: e.target.checked })} />
                          {t('documents.grid.optional')}
                        </label>
                        <label className="flex items-center gap-1">
                          <input type="checkbox" disabled={!editierbar} tabIndex={-1} checked={z.isAlternative} onChange={(e) => setze(z.key, { isAlternative: e.target.checked })} />
                          {t('documents.grid.alternative')}
                        </label>
                        {z.children.length > 0 && (
                          <select
                            disabled={!editierbar}
                            tabIndex={-1}
                            value={z.subMode ?? 'SUM_OF_CHILDREN'}
                            onChange={(e) => setze(z.key, { subMode: e.target.value as GridLine['subMode'] })}
                            className="rounded border border-input bg-background px-1 text-xs"
                          >
                            <option value="SUM_OF_CHILDREN">{t('documents.subModes.SUM_OF_CHILDREN')}</option>
                            <option value="OWN_PRICE">{t('documents.subModes.OWN_PRICE')}</option>
                          </select>
                        )}
                        {/* N-93/N-94: aufklappen, was selten gebraucht wird. Gibt die Liste „immer auf“ oder „immer zu“ vor,
                            entfällt der Schalter je Zeile; zugeklappt bleibt der Punkt als Hinweis auf gefüllte Felder. */}
                        {klappModus === 'auto' ? (
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => schalteWeitere(z.key)}
                            aria-expanded={weitereOffen.has(z.key)}
                            className={`inline-flex items-center gap-0.5 hover:text-foreground ${hatWeitere(z) ? 'text-foreground' : ''}`}
                            title={hatWeitere(z) ? t('documents.grid.moreFieldsFilled') : t('documents.grid.moreFieldsHint')}
                          >
                            {weitereOffen.has(z.key) ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                            {t('documents.grid.moreFields')}
                            {hatWeitere(z) && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />}
                          </button>
                        ) : (
                          klappModus === 'zu' &&
                          hatWeitere(z) && (
                            <span className="inline-flex items-center gap-1 text-foreground" title={t('documents.grid.moreFieldsHidden')}>
                              {t('documents.grid.moreFields')}
                              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                            </span>
                          )
                        )}
                      </div>
                    )}
                  </td>
                  <td className="relative px-1.5 py-1">
                    {mitWerten && (
                      <input
                        disabled={!editierbar}
                        value={z.articleNumber}
                        onChange={(e) => sucheArtikel(z.key, e.target.value)}
                        onBlur={() => setTimeout(() => setArtikelSuche((s) => (s?.key === z.key ? null : s)), 150)}
                        className={`${zelle} font-mono ${grau}`}
                        placeholder={t('documents.grid.articleSearchShort')}
                      />
                    )}
                    {artikelSuche?.key === z.key && artikelSuche.treffer.length > 0 && (
                      <ul
                        className="absolute left-0 z-50 mt-1 max-h-60 w-80 overflow-auto rounded-md border border-border shadow-lg"
                        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
                      >
                        {artikelSuche.treffer.map((a) => (
                          <li key={a.id}>
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => waehleArtikel(z, a)}
                              className="flex w-full gap-2 px-2 py-1.5 text-left text-xs hover:bg-accent"
                            >
                              <span className="w-20 font-mono">{a.articleNumber}</span>
                              <span className="flex-1">{a.name}</span>
                              {a.priceSource === 'CUSTOMER' && (
                                <span className="self-center rounded bg-primary/10 px-1 py-px text-[10px] font-medium text-primary">
                                  {t('documents.grid.customerPrice')}
                                </span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  {zeigePreise && (
                    <>
                      <td className="px-1.5 py-1">
                        {/* N-91: derselbe Baustein wie in der Stückliste — ohne Fokus `1.385,50`, beim Tippen die rohe Zahl. */}
                        {preisFeld && (
                          <GeldFeld disabled={preisGesperrt} wert={z.unitPrice} onWert={(t) => setze(z.key, { unitPrice: t })} className={`${zelle} ${grau}`} />
                        )}
                        {abzug && !abzugProzent && (
                          <GeldFeld disabled={!editierbar} wert={z.unitPrice} onWert={(t) => setze(z.key, { unitPrice: t })} className={zelle} />
                        )}
                      </td>
                      <td className="px-1.5 py-1">
                        {preisFeld && (
                          <PreiseinheitFeld
                            wert={z.priceUnit}
                            onWert={(zahl) => setze(z.key, { priceUnit: zahl })}
                            disabled={preisGesperrt}
                            title={t('documents.grid.priceUnitHint')}
                            className={`${zelle} text-right tabular-nums ${grau}`}
                          />
                        )}
                      </td>
                      <td className="px-1.5 py-1">
                        {preisFeld && (
                          <input disabled={preisGesperrt} value={z.discountPercent} onChange={(e) => setze(z.key, { discountPercent: e.target.value })} className={`${zelle} text-right tabular-nums ${grau}`} inputMode="decimal" />
                        )}
                        {abzug && abzugProzent && (
                          <input disabled={!editierbar} value={z.discountPercent} onChange={(e) => setze(z.key, { discountPercent: e.target.value })} className={`${zelle} text-right tabular-nums`} inputMode="decimal" placeholder="%" />
                        )}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right tabular-nums ${
                          tiefe === 1 || (!summeDerKinder && !abzug && !r?.countsToTotal) ? 'text-muted-foreground' : 'font-semibold'
                        }`}
                      >
                        {r && !r.withoutAmounts && (z.lineType === 'ARTICLE' || z.lineType === 'SUBTOTAL' || abzug) ? formatBetrag(r.lineNet, waehrung) : ''}
                      </td>
                      <td className="px-1.5 py-1">
                        {preisFeld && (
                          <input disabled={!editierbar} value={z.taxRate} onChange={(e) => setze(z.key, { taxRate: e.target.value })} className={`${zelle} text-right tabular-nums ${grau}`} inputMode="decimal" placeholder={zahlText(props.vorgabeSatz, 2)} />
                        )}
                      </td>
                    </>
                  )}
                  {props.projekteVerfuegbar && (
                    <>
                      <td className="px-1.5 py-1">
                        {mitWerten && (
                          <select
                            disabled={!editierbar}
                            value={z.projectId ?? ''}
                            onFocus={() => positionenLaden(z.projectId)}
                            onChange={(e) => {
                              const p = projekte.find((x) => x.id === e.target.value)
                              setze(z.key, { projectId: p?.id ?? null, projectNumber: p?.projectNumber ?? null, projectPositionId: null, projectPositionName: null, projectPositionNumber: null })
                              positionenLaden(p?.id ?? null)
                            }}
                            className={`${zelle} ${grau}`}
                          >
                            <option value="">—</option>
                            {z.projectId && !projekte.some((p) => p.id === z.projectId) && <option value={z.projectId}>{z.projectNumber}</option>}
                            {projekte.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.projectNumber}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-1.5 py-1">
                        {mitWerten && z.projectId && (
                          <select
                            disabled={!editierbar}
                            value={z.projectPositionId ?? ''}
                            onFocus={() => positionenLaden(z.projectId)}
                            onChange={(e) => {
                              const p = positionen[z.projectId!]?.find((x) => x.id === e.target.value)
                              setze(z.key, { projectPositionId: p?.id ?? null, projectPositionName: p?.name ?? null, projectPositionNumber: p?.nummer != null ? String(p.nummer) : null })
                            }}
                            className={`${zelle} ${grau}`}
                          >
                            <option value="">—</option>
                            {z.projectPositionId && !positionen[z.projectId]?.some((p) => p.id === z.projectPositionId) && (
                              <option value={z.projectPositionId}>{`${z.projectPositionNumber ?? ''} ${z.projectPositionName ?? ''}`}</option>
                            )}
                            {(positionen[z.projectId] ?? []).map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.nummer ?? ''} {p.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    </>
                  )}
                  <td className="whitespace-nowrap px-1.5 py-1 text-right">
                    {editierbar && (
                      <span className="inline-flex items-center gap-0.5 text-muted-foreground">
                        {(z.lineType === 'ARTICLE' || z.lineType === 'TEXT') &&
                          (
                            <label className="cursor-pointer p-0.5 hover:text-primary" title={t('documents.grid.photo')}>
                              <Camera className="h-3.5 w-3.5" />
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="hidden"
                                onChange={(e) => {
                                  const datei = e.target.files?.[0]
                                  if (datei) props.onFoto(z.key, datei)
                                  e.target.value = ''
                                }}
                              />
                            </label>
                          )}
                        {(z.lineType === 'ARTICLE' || z.lineType === 'TEXT') &&
                          (
                            <label className="cursor-pointer p-0.5 hover:text-primary" title={t('documents.grid.attachFile')}>
                              <Paperclip className="h-3.5 w-3.5" />
                              <input
                                type="file"
                                multiple
                                accept={DATEI_ENDUNGEN}
                                className="hidden"
                                onChange={(e) => {
                                  const dateien = Array.from(e.target.files ?? [])
                                  if (dateien.length) props.onDateien(z.key, dateien)
                                  e.target.value = ''
                                }}
                              />
                            </label>
                          )}
                        {tiefe === 1 ? (
                          <button type="button" tabIndex={-1} className="p-0.5 hover:text-primary" title={t('documents.grid.outdent')} aria-label={t('documents.grid.outdent')} onClick={() => onChange(ausruecken(zeilen, z.key))}>
                            <ChevronLeft className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <button type="button" tabIndex={-1} className="p-0.5 hover:text-primary" title={t('documents.grid.indent')} aria-label={t('documents.grid.indent')} onClick={() => onChange(einruecken(zeilen, z.key))}>
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button type="button" tabIndex={-1} className="p-0.5 hover:text-primary" title={t('documents.grid.moveUp')} aria-label={t('documents.grid.moveUp')} onClick={() => onChange(verschiebe(zeilen, z.key, -1))}>
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" tabIndex={-1} className="p-0.5 hover:text-primary" title={t('documents.grid.moveDown')} aria-label={t('documents.grid.moveDown')} onClick={() => onChange(verschiebe(zeilen, z.key, 1))}>
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" tabIndex={-1} className="p-0.5 hover:text-destructive" title={t('common.remove')} onClick={() => loesche(new Set([z.key]))}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    )}
                  </td>
                </RasterZeile>
                {zusatzOffen && (
                  // N-97 Zeile danach: Liefertermin und Anmerkung **neben** dem Langtext —
                  // die linken Zellen der Positionszeile reichen über beide Zeilen.
                  <tr className={`border-b border-border align-top last:border-0 ${tönung} ${abgeblendetHier}`}>
                    <td colSpan={zusatzUnten.termin} className="px-1.5 pb-1">
                      <div className="flex flex-wrap items-end gap-x-2 gap-y-1" title={t('documents.grid.lineDeviationHint')}>
                        <label className="block w-32 min-w-0">
                          {zusatzLabel(t('documents.grid.lineDeliveryDate'))}
                          <input
                            type="date"
                            disabled={!editierbar}
                            tabIndex={-1}
                            value={z.deliveryDate}
                            onChange={(e) => setze(z.key, { deliveryDate: e.target.value })}
                            onKeyDown={zusatzWeiter(() => anmerkungVon(z.key))}
                            className={`${zelle} text-xs`}
                          />
                        </label>
                        <label className="block min-w-[7rem] flex-1">
                          {zusatzLabel(t('documents.grid.lineDeliveryNote'))}
                          <input
                            disabled={!editierbar}
                            data-feld="deliveryNote"
                            tabIndex={-1}
                            value={z.deliveryNote}
                            onChange={(e) => setze(z.key, { deliveryNote: e.target.value })}
                            onKeyDown={zusatzWeiter(() => mengeDerNaechsten(z.key))}
                            className={`${zelle} text-xs`}
                            maxLength={500}
                          />
                        </label>
                      </div>
                    </td>
                    <td colSpan={zusatzUnten.rest} />
                  </tr>
                )}
              </Fragment>
            )
          })}
          {zeilen.length === 0 && (
            <tr>
              <td colSpan={spalten + 1} className="px-4 py-6 text-center text-sm text-muted-foreground">
                {t('documents.grid.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </ZiehBereich>
      {editierbar && (
        <div className="flex flex-wrap gap-2 border-t border-border bg-muted/20 px-2 py-1.5 text-xs" data-auswahl-behalten>
          {TYPEN.filter((typ) => typ !== 'DEDUCTION' || zeigePreise).map((typ) => (
            <button key={typ} type="button" onClick={() => neueNach([...auswahl].at(-1) ?? null, typ)} className="rounded border border-input px-2 py-0.5 hover:bg-muted">
              + {t(`documents.lineTypes.${typ}`)}
            </button>
          ))}
          <span className="ml-auto self-center text-muted-foreground">{t('documents.grid.keyboardHint')}</span>
        </div>
      )}
    </div>
  )
}
